import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { CustomAttributeInfo, DataElement } from '@store/user/user.model';
import { DatePickerHeaderComponent } from '@shared/components/forms/datepicker/date-picker-header/date-picker-header.component';
import { ComponentType } from '@angular/cdk/portal';
import { DATE_VALIDATION_MESSAGES } from '@shared/validators/validation.messages';
import { LookUpData, MULTI_COUNTY, OperatorValue, RecentSearches, RENDER_ID, Shape } from '@store/search-board/search-board.model';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateStringRange, OpenModalPositionService } from '@shared/services/open-modal-position.service';
import { ModalType } from '@shared/models/modal.model';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import { selectDrawingShapes, selectLookupsByFieldCode, selectRecentSearches, selectSidePanelTab } from '@store/search-board/search-board.selector';
import { FieldCode, STATE_TOKEN_NUMBER } from '@root/search/models/display-fields.constant';
import { selectCurrentCounties, selectIsRegionMultiState } from '@store/user/user.selector';
import { GetLookUps } from '@store/search-board/search-board.actions';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchService } from '@root/search/services/search.service';

const SUGGESTION_DEBOUNCE_TIME = 100;
const FIELD_TYPE_CHANGE_TIME = 100;

@UntilDestroy()
@Component({
  selector: 'rlst-search-form-list',
  templateUrl: './search-form-list.component.html',
  styleUrls: ['./search-form-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchFormListComponent implements OnInit {
  @Input() dataElement: DataElement;
  @Input() set searchList(value: UntypedFormArray) {
    this._searchList = value;
    this.operatorChangeSubscription.unsubscribe();
    this.operatorChangeSubscription = new Subscription();
    for (const control of value.controls) {
      if (control instanceof UntypedFormGroup) {
        this.subscribeToOperatorValueChanges(control);
      }
    }
  }

  get searchList(): UntypedFormArray {
    return this._searchList;
  }

  private _searchList: UntypedFormArray;
  @Input() disabledMessage = '';
  @Input() expanded: boolean;
  @Input() disabled = false;
  @Input() defaultOperatorValueList: Array<OperatorValue>;
  @Output() operatorValueChanged: EventEmitter<AbstractControl> = new EventEmitter();
  @Output() fieldAdded: EventEmitter<AbstractControl> = new EventEmitter();

  public get isControlDisabled(): boolean {
    return this.disabled;
  }

  public lookups$: Observable<Array<LookUpData>>;
  public lookups: Array<LookUpData>;
  public readonly emptyLookups: Array<LookUpData> = [];
  public maxNumberMultiSelection$: Observable<number>;
  public disabled$: Observable<[boolean, string]>;
  public recentSearches$: Observable<RecentSearches>;
  private operatorChangeSubscription: Subscription = new Subscription();
  private currentCounty$: Observable<string>;
  private isRegionMultiState$: Observable<boolean>;
  private parentHasMapAttribute$: Observable<boolean>;
  private readonly valueFrom$: Subject<string> = new Subject();
  private tabSelected: string;

  public readonly RENDER_ID: typeof RENDER_ID = RENDER_ID;
  public readonly SINGLE_VALUE_FIELD: number = 1;
  public readonly UNLIMITED_VALUES_FIELD: number = 0;
  public openModal$: Observable<boolean>;
  public isLoadStarted: boolean = false;
  public popoverHtml: string;

  public customHeader: ComponentType<{}> = DatePickerHeaderComponent;

  constructor(private readonly cdr: ChangeDetectorRef,
    private readonly openModalService: OpenModalPositionService,
    private readonly store$: Store<AppState>,
    private readonly mixpanelService: MixpanelService,
    private readonly searchService: SearchService
  ) {
  }

  ngOnInit(): void {
    this.popoverHtml = `<b>Sell Score Ratings and Values:</b><br/>
    <b>Very High:</b> 831-1000<br/>
    <b>High:</b> 625-830<br/>
    <b>Moderate:</b> 502-624<br/>
    <b>Low:</b> 354-501<br/>
    <b>Very Low:</b> 0-353`;

    this.lookups$ = this.store$.pipe(select(selectLookupsByFieldCode, (this.dataElement.fieldCode as FieldCode)));
    this.lookups$
      .pipe(untilDestroyed(this))
      .subscribe((lookups: Array<LookUpData>) => {
        this.lookups = lookups;
        if (lookups) {
          this.isLoadStarted = true;
        }

        this.cdr.markForCheck();
      });
    this.maxNumberMultiSelection$ = this.lookups$.pipe(
      map((lookups: Array<LookUpData>) => lookups ? Math.min(this.dataElement.numberOfSearchValues, lookups.length) : 0)
    );
    this.currentCounty$ = this.store$.pipe(select(selectCurrentCounties));
    this.isRegionMultiState$ = this.store$.pipe(select(selectIsRegionMultiState));
    this.parentHasMapAttribute$ = this.store$.pipe(
      select(selectDrawingShapes),
      map((shapes: Array<Shape>) => !!shapes.length)
    );

    this.disabled$ = combineLatest(
      [
        this.lookups$,
        this.currentCounty$,
        this.isRegionMultiState$,
        this.parentHasMapAttribute$
      ]
    ).pipe(
      map(([lookups, currentCounty, isRegionMultiState, parentHasMapAttribute]: [Array<LookUpData>, string, boolean, boolean]) => {
        if (isRegionMultiState && (this.dataElement.tokenNumber === STATE_TOKEN_NUMBER)) {
          return [true, 'This attribute is not available when two or more States are selected'];
        }
        if ((currentCounty === MULTI_COUNTY) && !this.dataElement.multiCounty) {
          return [true, 'Search attribute is not available when multiple counties are selected'];
        }
        if (this.isSelectableField(this.dataElement.displayInfo.renderId) &&
          !lookups &&
          this.dataElement.displayInfo.renderId !== RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP) {
          return [true, ''];
        }
        // A multi-select search lookup doesn't have attributes to begin with until the user searches for a value
        if (this.dataElement.displayInfo.renderId === RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP) {
          return [false, ''];
        }
        if (this.isSelectableField(this.dataElement.displayInfo.renderId) && lookups && !lookups.length) {
          return [true, 'Attribute does not have any values'];
        }
        if (parentHasMapAttribute && this.dataElement.geo) {
          return [true, 'Search attribute is not available when using Shape Drawing Tools outside of the location'];
        }
        return [false, ''];
      })
    );

    this.openModal$ = this.lookups$.pipe(
      map((lookups: Array<LookUpData>) => lookups && lookups.length > 0 && !this.isLoadStarted),
      filter<boolean>(Boolean),
      take(1)
    );

    this.recentSearches$ = this.dataElement.fieldCode !== 'SITE_ADDRESS' ? combineLatest([
      this.valueFrom$.asObservable(),
      this.store$.pipe(select(selectRecentSearches))
    ]).pipe(
      debounceTime(SUGGESTION_DEBOUNCE_TIME),
      map(([value, recentSearches]) => {
        return (recentSearches[this.dataElement.fieldCode] || []).filter((recentSearch: string) => {
          return value && recentSearch.startsWith(value);
        });
      }))
      : this.valueFrom$.asObservable().pipe(
          debounceTime(SUGGESTION_DEBOUNCE_TIME),
          switchMap(val => val?.length >= 3 ? this.searchService.searchAddress(val as string) : of([])));

    this.store$.select(selectSidePanelTab).subscribe((sidePanelTab) => this.tabSelected = sidePanelTab);
  }

  public onSelectClick(event: Event) {
    event.stopImmediatePropagation();
  }

  public addField(searchFieldGroup: AbstractControl, dataElement: DataElement): void {
    if (!searchFieldGroup.valid) {
      return;
    }
    if (!this.isLimitReached(dataElement)) {
      this.fieldAdded.emit(searchFieldGroup);
      this.subscribeToOperatorValueChanges(this.searchList.at(this.searchList.length - 1));
    }
  }

  public removeField(index: number): void {
    this.searchList.removeAt(index);
  }

  public isLimitReached(dataElement: DataElement): boolean {
    return this.isMultiValue(dataElement)
      && dataElement.numberOfSearchValues !== this.UNLIMITED_VALUES_FIELD
      && this.searchList.length > dataElement.numberOfSearchValues;
  }

  public isMultiValue(dataElement: DataElement): boolean {
    return dataElement.numberOfSearchValues !== this.SINGLE_VALUE_FIELD;
  }

  public isSelectableField(renderId: number): boolean {
    return renderId === RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP ||
      renderId === RENDER_ID.DIALOGBOX_LOOKUP_WITH_DATA_LOAD ||
      renderId === RENDER_ID.DIALOGBOX_LOOKUP ||
      renderId === RENDER_ID.SINGLE_SELECT ||
      renderId === RENDER_ID.DROPDOWN_MENU;
  }

  public hasErrors(searchFieldGroup: AbstractControl) {
    return (searchFieldGroup.get('values').getError('validationError') ||
      searchFieldGroup.get(['values', 'valueFrom']).getError('validationError') ||
      searchFieldGroup.get(['values', 'valueFrom']).getError('matDatepickerParse') ||
      (searchFieldGroup.get(['values', 'valueTo']) &&
        (searchFieldGroup.get(['values', 'valueTo']).getError('validationError') ||
          searchFieldGroup.get(['values', 'valueTo']).getError('matDatepickerParse'))));
  }

  public getDatepickerErrorMessage(): string {
    return DATE_VALIDATION_MESSAGES.invalidCharError;
  }

  private subscribeToOperatorValueChanges(control: AbstractControl): void {
    this.operatorChangeSubscription.add(
      control.get('operator').valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.operatorValueChanged.emit(control);
        setTimeout(() => {
          this.mixpanelService.track('Search - Field Type Click', {
            searchType: this.tabSelected,
            fieldName: this.dataElement.fieldCode,
            fieldType: control.value.operator
          });
        }, FIELD_TYPE_CHANGE_TIME);
      }));

    this.operatorChangeSubscription.add(
      control.get('values.valueFrom').valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
        this.valueFrom$.next(value);
      }));
  }

  public openDateRangePicker(event: MouseEvent, searchFieldGroup: AbstractControl, customAttributeInfo: CustomAttributeInfo) {
    const matData = {
      data: {
        dateData: {
          ...searchFieldGroup.get('values').value
        },
        customAttributeInfo
      },
      panelClass: ModalType.DATE_RANGE_PICKER,
    };
    this.openModalService.openDateRangePicker(event.target as HTMLElement, matData)
      .pipe(filter(Boolean))
      .subscribe((data: DateStringRange) => {
        searchFieldGroup.get('values').setValue({ valueFrom: data.fromDate, valueTo: data.toDate });
        this.cdr.markForCheck();
      });
  }

  public loadLookups(): void {
    if (!this.isLoadStarted) {
      this.store$.dispatch(new GetLookUps([this.dataElement]));
      this.isLoadStarted = true;
    }
  }

  public isAddButtonDisabled(searchFieldGroup: AbstractControl): boolean {
    return searchFieldGroup.invalid || !searchFieldGroup.get('values.valueFrom').value;
  }
}
